import React, { useEffect, useState } from 'react';
import { ChoiceInput } from '@unmind/design-system';
import useFeatureFlag from '../../../flags/useFeatureFlag';
import { useFilterState } from '../FilterState';
import { UserGroupFilterStateKey } from '../FilterState/types';
import FilterDropdown from './FilterDropdown';
import TimeFilterDropdown from './TimeFilterDropdown';
import useLoadFilterOptions from './useLoadFilterOptions';
import { RadioContainer, Panel, FilterLabel } from './FilterPanel.styled';

const radioOptions: { label: string; value: UserGroupFilterStateKey }[] = [
  {
    label: 'Locations',
    value: 'locationIds',
  },
  {
    label: 'Departments',
    value: 'departmentIds',
  },
];

const FilterPanel = () => {
  const {
    timeFilterState,
    setUserGroupFilterType,
    userGroupFilterType,
    tabType,
    timeFilterPreset,
  } = useFilterState();

  const { availableFilterTypes, dashboardFilterOptions, loading } =
    useLoadFilterOptions({
      userGroupFilterType,
      timeFilterState,
      tabType,
      timeFilterPreset,
    });
  const [isRadioVisible, setRadioVisible] = useState<boolean>(false);

  const isFiltersFeatureEnabled = useFeatureFlag(
    'admin-dashboards-show-filters',
  );

  const isTimeFilterFeatureEnabled = useFeatureFlag(
    'admin-dashboards-enable-time-filtering',
  );

  useEffect(() => {
    if (!isFiltersFeatureEnabled) {
      return setRadioVisible(false);
    }

    if (loading) {
      return;
    }

    if (availableFilterTypes.length === 1) {
      if (userGroupFilterType) {
        return;
      }

      const [validFilterType] = availableFilterTypes;
      setUserGroupFilterType(validFilterType, true);

      return setRadioVisible(false);
    }

    if (availableFilterTypes.length > 1) {
      return setRadioVisible(true);
    }
  }, [
    dashboardFilterOptions,
    isFiltersFeatureEnabled,
    loading,
    setUserGroupFilterType,
    userGroupFilterType,
    availableFilterTypes,
  ]);

  if (loading) {
    return null;
  }

  const handleRadioChange = (newFilterType: UserGroupFilterStateKey) => {
    setUserGroupFilterType(newFilterType);
  };

  return (
    <Panel>
      {isRadioVisible ? (
        <RadioContainer>
          <FilterLabel>Filter</FilterLabel>
          {radioOptions.map(({ label, value }) => (
            <ChoiceInput
              key={value}
              id={value}
              name="user-group-filter-type"
              type="radio"
              label={label}
              checked={userGroupFilterType === value}
              aria-checked={userGroupFilterType === value}
              onChange={() => handleRadioChange(value)}
            />
          ))}
        </RadioContainer>
      ) : null}
      {userGroupFilterType ? (
        <FilterDropdown
          options={dashboardFilterOptions[userGroupFilterType]}
          filterType={userGroupFilterType}
          tabType={tabType}
        />
      ) : null}
      {isTimeFilterFeatureEnabled ? <TimeFilterDropdown /> : null}
    </Panel>
  );
};

export default FilterPanel;
